import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { UpgradePlanButton } from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { useAutoRefresh } from '@/Domain/hooks/KnowledgeBase/useAutoRefresh.hook';
import { formatDateToTimeAgo } from '@/Domain/utils/formatDateToTimeAgo';

export const AutoRefreshCard = () => {
  const { t } = useTranslation(['knowledge_base']);
  const { settings, isLoading, setEnabled, isPending } = useAutoRefresh();

  if (isLoading || !settings) return null;

  const { enabled, available, last_check_timestamp, watched_url_count, url_article_count } =
    settings;

  // No URL articles at all: don't sell auto-refresh on an empty KB. Kept
  // mounted while a toggle mutation is in flight so the switch can't vanish
  // mid-click on an optimistic update.
  if (!enabled && url_article_count === 0 && !isPending) return null;

  return (
    <div
      className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4"
      data-testid="kb-auto-refresh-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
          <RefreshCw className="h-4 w-4 text-foreground" />
        </div>
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-foreground">
            {t('knowledge_base:auto_refresh.title')}
          </p>
          <p className="max-w-xl text-xs text-muted-foreground">
            {t('knowledge_base:auto_refresh.description')}
          </p>
          {enabled && last_check_timestamp && (
            <p
              className="max-w-xl text-xs text-foreground-subtle"
              data-testid="kb-auto-refresh-caption"
            >
              {t('knowledge_base:auto_refresh.caption', {
                lastCheck: formatDateToTimeAgo(last_check_timestamp),
                count: watched_url_count,
              })}
            </p>
          )}
        </div>
      </div>
      {available ? (
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={isPending}
          aria-label={t('knowledge_base:auto_refresh.title')}
          data-testid="kb-auto-refresh-switch"
        />
      ) : (
        <UpgradePlanButton size="sm" source="kb_auto_refresh_banner" />
      )}
    </div>
  );
};
